import React, { useMemo, useState } from "react";
import { Modal, Table, Button as AntdButton, Collapse, Tooltip, message } from "antd";
import type { ColumnsType } from "antd/es/table";
import {
  BulkOperationFailure,
  BulkOperationResultsSummary,
  categoryDisplayLabel,
  formatBulkOperationFailureReport,
  groupBulkOperationFailures,
} from "./bulkOperationResultsTypes";
import { ScheduleSideBadge, SchedulePlacementStack } from "@/features/channels/scheduleSideDisplay";
import styles from "./BulkOperationResultsModal.module.scss";

export interface BulkOperationResultsModalProps {
  open: boolean;
  summary: BulkOperationResultsSummary | null;
  onClose: () => void;
  onRetryRetryable?: (ids: string[]) => void;
  retryBusy?: boolean;
}

function EllipsisText({ text }: { text: string }) {
  const value = text || "—";
  return (
    <Tooltip title={value}>
      <span className={styles.ellipsisCell}>{value}</span>
    </Tooltip>
  );
}

function FailureDetailModal({
  open,
  row,
  summary,
  onClose,
}: {
  open: boolean;
  row: BulkOperationFailure | null;
  summary: BulkOperationResultsSummary;
  onClose: () => void;
}) {
  if (!row) return null;
  const reasonTitle = categoryDisplayLabel(row.category);
  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={
        <AntdButton type="primary" className={styles.brandPrimary} onClick={onClose}>
          Close
        </AntdButton>
      }
      width="min(480px, calc(100vw - var(--kk-sidebar-width, 250px) - 48px))"
      destroyOnClose
      title={reasonTitle}
      className={`${styles.shell} kk-modal-center-in-content kk-modal-align-page-card`}
    >
      <p className={styles.detailBody}>{row.reasonDetail}</p>
      <div className={styles.detailGrid}>
        <div className={styles.detailLabel}>{summary.firstColumnLabel}</div>
        <div className={styles.detailValue}>{row.first || "—"}</div>
        {summary.secondColumnLabel ? (
          <>
            <div className={styles.detailLabel}>{summary.secondColumnLabel}</div>
            <div className={styles.detailValue}>{row.second || "—"}</div>
          </>
        ) : null}
        {row.idLabel ? (
          <>
            <div className={styles.detailLabel}>Reference</div>
            <div className={styles.detailValue}>{row.idLabel}</div>
          </>
        ) : null}
        {summary.showChannelColumns ? (
          <>
            <div className={styles.detailLabel}>Schedule</div>
            <div className={styles.detailValue}>
              {row.scheduleSide ? (
                <ScheduleSideBadge side={row.scheduleSide} />
              ) : (
                "—"
              )}
            </div>
            <div className={styles.detailLabel}>Channel</div>
            <div className={styles.detailValue}>{row.channel || "—"}</div>
            <div className={styles.detailLabel}>Rotation</div>
            <div className={styles.detailValue}>{row.rotation || "—"}</div>
          </>
        ) : null}
        <div className={styles.detailLabel}>Retryable</div>
        <div className={styles.detailValue}>{row.retryable ? "Yes" : "No"}</div>
        <div className={styles.detailLabel}>Next action</div>
        <div className={styles.detailValue}>
          {row.nextActionDetail || row.nextAction}
        </div>
      </div>
    </Modal>
  );
}

function CompactFailureTable({
  rows,
  summary,
  onOpenReason,
}: {
  rows: BulkOperationFailure[];
  summary: BulkOperationResultsSummary;
  onOpenReason: (row: BulkOperationFailure) => void;
}) {
  const columns: ColumnsType<BulkOperationFailure> = [
    {
      title: summary.firstColumnLabel,
      key: "first",
      width: 160,
      ellipsis: true,
      render: (_: unknown, row) => <EllipsisText text={row.first || "—"} />,
    },
  ];

  if (summary.secondColumnLabel) {
    columns.push({
      title: summary.secondColumnLabel,
      key: "second",
      width: 180,
      ellipsis: true,
      render: (_: unknown, row) => (
        <Tooltip title={row.idLabel ? `${row.second || "—"} (${row.idLabel})` : row.second || "—"}>
          <span className={styles.ellipsisCell}>{row.second || "—"}</span>
        </Tooltip>
      ),
    });
  }

  columns.push(
    {
      title: "Reason",
      key: "reason",
      width: 220,
      ellipsis: true,
      render: (_: unknown, row) => (
        <button
          type="button"
          className={styles.reasonLink}
          onClick={() => onOpenReason(row)}
          title={row.reasonDetail || "View full explanation"}
        >
          {row.reasonLabel || categoryDisplayLabel(row.category)}
        </button>
      ),
    },
  );

  if (summary.showChannelColumns) {
    columns.push(
      {
        title: "Schedule",
        key: "scheduleSide",
        width: 96,
        render: (_: unknown, row) =>
          row.scheduleSide ? (
            <ScheduleSideBadge side={row.scheduleSide} compact />
          ) : (
            "—"
          ),
      },
      {
        title: "Channel",
        key: "channel",
        width: 130,
        ellipsis: true,
        render: (_: unknown, row) => <EllipsisText text={row.channel || "—"} />,
      },
      {
        title: "Rotation",
        key: "rotation",
        width: 120,
        ellipsis: true,
        render: (_: unknown, row) => <EllipsisText text={row.rotation || "—"} />,
      },
    );
  }

  columns.push(
    {
      title: "Retryable",
      dataIndex: "retryable",
      key: "retryable",
      width: 78,
      render: (v: boolean) => (v ? "Yes" : "No"),
    },
    {
      title: "Next Action",
      key: "nextAction",
      width: 140,
      ellipsis: true,
      render: (_: unknown, row) => <EllipsisText text={row.nextAction} />,
    },
  );

  return (
    <Table
      className={styles.compactTable}
      size="small"
      pagination={rows.length > 50 ? { pageSize: 50, size: "small" } : false}
      rowKey={(row) =>
        `${row.id}::${row.scheduleSide || ""}::${row.rotation || ""}::${row.channel || ""}`
      }
      dataSource={rows}
      columns={columns}
      // Min table width for column layout; horizontal scroll stays inside the
      // table — never widen the modal / page past the content column.
      scroll={{ x: summary.showChannelColumns ? 1040 : 760 }}
    />
  );
}

/**
 * Standard Super Admin operation-results dialog (single or multi).
 * Use for Songs, Albums, Artists, Record Labels, and future surfaces.
 */
export function BulkOperationResultsModal({
  open,
  summary,
  onClose,
  onRetryRetryable,
  retryBusy,
}: BulkOperationResultsModalProps) {
  const groups = useMemo(
    () => (summary ? groupBulkOperationFailures(summary.failures) : []),
    [summary],
  );
  const [expanded, setExpanded] = useState<string[]>([]);
  const [detailRow, setDetailRow] = useState<BulkOperationFailure | null>(null);

  const title = summary ? `${summary.actionLabel} — results` : "Operation results";
  const retryIds = Array.from(
    new Set(
      (summary?.failures || []).filter((f) => f.retryable).map((f) => f.id),
    ),
  );

  const copyReport = async () => {
    if (!summary) return;
    try {
      await navigator.clipboard.writeText(formatBulkOperationFailureReport(summary));
      message.success("Failure report copied");
    } catch {
      message.error("Could not copy report");
    }
  };

  return (
    <>
      <Modal
        open={open}
        onCancel={onClose}
        footer={null}
        width="min(1100px, calc(100vw - var(--kk-sidebar-width, 250px) - 48px))"
        destroyOnClose
        title={title}
        className={`${styles.shell} kk-modal-center-in-content kk-modal-align-page-card`}
      >
        {!summary ? null : (
          <div>
            <p className={styles.intro}>
              Review successes and failures below. Channel Continuity issues are intentional —
              add replacement music before retrying those{" "}
              {summary.entityNounPlural.toLowerCase()}. This report stays available until
              you dismiss it or leave the page.
            </p>
            <div className={styles.summaryStrip}>
              <span>
                Requested: <strong>{summary.requested}</strong>
              </span>
              <span>
                Succeeded: <strong>{summary.succeeded}</strong>
              </span>
              <span>
                Failed: <strong>{summary.failed}</strong>
              </span>
              <span>
                Skipped: <strong>{summary.skipped}</strong>
              </span>
              <span>
                Retryable: <strong>{summary.retryable}</strong>
              </span>
            </div>

            {summary.failed === 0 ? (
              <p className={styles.successCopy}>
                All requested {summary.entityNounPlural.toLowerCase()} completed
                successfully
                {summary.skipped > 0
                  ? ` (${summary.skipped} already in the desired state and skipped).`
                  : "."}
              </p>
            ) : (
              <Collapse
                size="small"
                activeKey={expanded}
                onChange={(keys) =>
                  setExpanded(Array.isArray(keys) ? keys.map(String) : [String(keys)])
                }
                items={groups.map((g) => ({
                  key: g.category,
                  label: (
                    <span>
                      <strong>{g.reasonLabel}</strong>
                      {" — "}
                      {g.count}{" "}
                      {g.count === 1 ? summary.entityNoun : summary.entityNounPlural}
                    </span>
                  ),
                  children: (
                    <div>
                      {g.channels.map((ch) => {
                        const channelRows = ch.rotations.flatMap((rot) => rot.rows);
                        return (
                          <div key={ch.key} style={{ marginBottom: 10 }}>
                            <div className={styles.groupMeta}>
                              <SchedulePlacementStack
                                scheduleSide={ch.scheduleSide}
                                channel={ch.channel}
                                rotation={null}
                                compact
                                showRotation={false}
                              />
                            </div>
                            <CompactFailureTable
                              rows={channelRows}
                              summary={summary}
                              onOpenReason={setDetailRow}
                            />
                          </div>
                        );
                      })}
                      {g.unscoped.length > 0 ? (
                        <div>
                          <div className={styles.groupMeta}>Other</div>
                          <CompactFailureTable
                            rows={g.unscoped}
                            summary={summary}
                            onOpenReason={setDetailRow}
                          />
                        </div>
                      ) : null}
                    </div>
                  ),
                }))}
              />
            )}

            <div className={styles.footerRow}>
              {summary.failed > 0 ? (
                <AntdButton onClick={() => void copyReport()}>Copy Failure Report</AntdButton>
              ) : null}
              {onRetryRetryable && retryIds.length > 0 ? (
                <AntdButton
                  loading={retryBusy}
                  onClick={() => onRetryRetryable(retryIds)}
                >
                  Retry {retryIds.length} retryable
                </AntdButton>
              ) : null}
              <AntdButton type="primary" className={styles.brandPrimary} onClick={onClose}>
                Close
              </AntdButton>
            </div>
          </div>
        )}
      </Modal>

      {summary ? (
        <FailureDetailModal
          open={Boolean(detailRow)}
          row={detailRow}
          summary={summary}
          onClose={() => setDetailRow(null)}
        />
      ) : null}
    </>
  );
}

export default BulkOperationResultsModal;
